import { clsx } from 'clsx'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import styles from './Button.module.css'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

type ButtonStyleOptions = {
  variant?: ButtonVariant
  size?: ButtonSize
  isFullWidth?: boolean
  /** Square tap box for a lone glyph. Requires an aria-label on the element. */
  isIconOnly?: boolean
  className?: string | undefined
}

/**
 * The class string on its own, for elements that are a button in looks but not
 * in markup — a TanStack `<Link>` CTA, most often:
 *
 *   <Link to="/sobrevivente" className={buttonClass({ variant: 'primary' })}>
 */
export function buttonClass({
  variant = 'primary',
  size = 'md',
  isFullWidth = false,
  isIconOnly = false,
  className,
}: ButtonStyleOptions = {}): string {
  return clsx(
    styles.btn,
    styles[variant],
    styles[size],
    isFullWidth && styles.isFullWidth,
    isIconOnly && styles.isIconOnly,
    className,
  )
}

type ButtonProps = ButtonStyleOptions &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & {
    /** Renders a spinner and blocks input; the label keeps its space. */
    isLoading?: boolean
    children?: ReactNode
  }

export function Button({
  variant = 'primary',
  size = 'md',
  isFullWidth = false,
  isIconOnly = false,
  isLoading = false,
  className,
  type = 'button',
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled === true || isLoading}
      aria-busy={isLoading || undefined}
      className={clsx(
        buttonClass({ variant, size, isFullWidth, isIconOnly, ...(className ? { className } : {}) }),
        isLoading && styles.isLoading,
      )}
    >
      {isLoading && <span className={styles.spinner} aria-hidden="true" />}
      {children}
    </button>
  )
}
